<template>
  <div class="player-page">
    <button class="back-btn" @click="goBack" title="返回" aria-label="返回">
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="15 18 9 12 15 6"></polyline>
      </svg>
    </button>

    <div class="player-container" v-if="src">
      <h2 class="video-title" v-if="title">{{ title }}</h2>
      <div v-if="statusText" class="status-tip">{{ statusText }}</div>
      <div ref="mountPoint" class="video-mount"></div>
    </div>
    <div class="empty-tip" v-else>暂无视频资源</div>
  </div>
</template>

<script>
var IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

export default {
  name: "VideoPlayer",
  props: {
    title: { type: String, default: "" },
    src: { type: String, required: true },
  },
  data: function() {
    return { statusText: "" };
  },
  mounted: function() {
    this.setupVideo();
  },
  methods: {
    // ---- 创建 video 元素 ----
    setupVideo: function() {
      var mount = this.$refs.mountPoint;
      if (!mount) return;

      var v = document.createElement("video");
      v.controls = true;
      v.setAttribute("playsinline", "true");
      v.setAttribute("webkit-playsinline", "true");
      v.setAttribute("x5-video-player-type", "h5");
      v.setAttribute("x5-playsinline", "true");
      v.style.cssText = "display:block;width:100%;background:#000";
      mount.appendChild(v);
      this._video = v;

      if (IS_IOS) {
        // iOS：必须 muted 才能 autoplay；播放开始后自动取消静音
        v.muted = true;
        this.iosPlay(v);
      } else {
        // 非 iOS：直接 URL + 自动播放（有声）
        v.src = this.src;
        v.autoplay = true;
        v.load();
        v.play().catch(function() {});
      }
    },

    // ---- iOS：先直连，失败走 blob 兜底 ----
    iosPlay: function(video) {
      var self = this;
      self.statusText = "连接中...";

      // 步骤1：尝试直连（原生 <video> 不受 Mixed Content 限制）
      video.src = this.src;
      video.load();

      var timedOut = false;
      var timer = setTimeout(function() {
        timedOut = true;
        // 8 秒后还没 metadata，直连不行，走 blob
        if (video.readyState === 0) {
          self.tryBlob(video);
        }
      }, 8000);

      video.addEventListener(
        "loadedmetadata",
        function() {
          clearTimeout(timer);
          if (timedOut) return;
          self.statusText = "";
          // iOS: 先用 muted 触发 autoplay，播放开始后取消静音
          video
            .play()
            .then(function() {
              video.muted = false;
            })
            .catch(function() {});
        },
        { once: true },
      );

      video.addEventListener(
        "error",
        function() {
          clearTimeout(timer);
          if (timedOut) return;
          // 直连报错，走 blob 下载
          self.tryBlob(video);
        },
        { once: true },
      );
    },

    // ---- blob 下载兜底 ----
    tryBlob: function(video) {
      var self = this;
      self.statusText = "正在下载视频...";

      var xhr = new XMLHttpRequest();
      xhr.open("GET", this.src, true);
      xhr.responseType = "blob";
      xhr.timeout = 300000;
      xhr.retryCount = 0;

      xhr.onprogress = function(e) {
        if (e.lengthComputable) {
          self.statusText =
            "正在下载视频 " + Math.round((e.loaded / e.total) * 100) + "%";
        }
      };

      function setBlobAndPlay(blob) {
        var url = URL.createObjectURL(blob);
        self._blobUrl = url;
        self.statusText = "";
        video.src = url;
        video.load();
        // iOS: muted 先触发 autoplay，播放成功后取消静音
        setTimeout(function() {
          video
            .play()
            .then(function() {
              video.muted = false;
            })
            .catch(function() {});
        }, 500);
      }

      xhr.onload = function() {
        if (xhr.response && xhr.response.size > 0) {
          setBlobAndPlay(xhr.response);
        } else {
          self.statusText = "视频加载失败，请检查视频文件是否正常";
        }
      };

      xhr.onerror = function() {
        // Mixed Content 静默失败（HTTPS 页面请求 HTTP 视频）
        // 尝试用 fetch + no-cors 模式（只能读，无法检查结果）
        self.statusText = "下载失败，尝试备用方式...";
        fetch(self.src, { mode: "cors" })
          .then(function(r) {
            return r.blob();
          })
          .then(function(blob) {
            if (blob.size > 0) setBlobAndPlay(blob);
            else throw new Error("Empty");
          })
          .catch(function() {
            self.statusText =
              "无法加载视频。请确保当前页面和视频使用相同的 HTTP/HTTPS 协议。";
          });
      };

      xhr.ontimeout = function() {
        self.statusText = "视频文件过大，下载超时（>5分钟）";
      };

      xhr.send();
    },

    goBack: function() {
      if (this.$router) {
        this.$router.back();
      } else {
        window.history.back();
      }
    },
  },
  beforeDestroy: function() {
    if (this._blobUrl) URL.revokeObjectURL(this._blobUrl);
    if (this._video && this._video.parentNode) {
      this._video.parentNode.removeChild(this._video);
    }
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
.player-page {
  position: relative;
  min-height: 100vh;
  background: #111;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}
.back-btn {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
}
.back-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
.back-btn:active {
  background: rgba(255, 255, 255, 0.2);
}
.player-container {
  max-width: 860px;
  width: 100%;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.7);
}
.video-title {
  padding: 16px 20px;
  background: #1a1a1a;
  color: #fff;
  font-size: 20px;
  font-weight: 500;
  border-bottom: 1px solid #333;
}
.video-mount {
  width: 100%;
}
.empty-tip {
  color: #888;
  font-size: 18px;
  text-align: center;
}
.status-tip {
  color: #aaa;
  font-size: 15px;
  text-align: center;
  padding: 32px 20px;
}
</style>
